import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/shared'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'
import CustomFieldFormDialog from '@/components/MarketingEmail/CustomFieldFormDialog'

const CustomFields = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editingField, setEditingField] = useState(null)
  const [deletingField, setDeletingField] = useState(null)

  const { data, isLoading } = useQuery('email-custom-fields', emailCustomFieldService.getAllFields, {
    onError: () => toast.error('Failed to fetch custom fields'),
  })
  const fields = data?.data?.fields || []

  const refresh = () => queryClient.invalidateQueries('email-custom-fields')

  const handleDelete = async () => {
    if (!deletingField) return
    try {
      await emailCustomFieldService.deleteField(deletingField.id)
      toast.success('Custom field deleted')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete custom field')
    } finally {
      setDeletingField(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Custom Fields"
        description="Extra contact attributes used for segmentation and email personalization"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/contacts')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Contacts
            </Button>
            <Button size="sm" onClick={() => { setEditingField(null); setFormOpen(true) }}>
              <Plus className="mr-2 h-4 w-4" />
              New Field
            </Button>
          </>
        }
      />

      <Card>
        <div className="rounded-lg border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="bg-muted/50 font-medium">Label</TableHead>
                <TableHead className="bg-muted/50 font-medium">Key</TableHead>
                <TableHead className="bg-muted/50 font-medium">Type</TableHead>
                <TableHead className="bg-muted/50 font-medium">Required</TableHead>
                <TableHead className="bg-muted/50 font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(5)].map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : fields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                    No custom fields yet.
                  </TableCell>
                </TableRow>
              ) : (
                fields.map((field) => (
                  <TableRow key={field.id} className="hover:bg-muted/50 border-border">
                    <TableCell className="font-medium">{field.label}</TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">{field.name}</TableCell>
                    <TableCell><Badge variant="secondary">{field.field_type}</Badge></TableCell>
                    <TableCell>{field.is_required ? 'Yes' : 'No'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingField(field); setFormOpen(true) }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeletingField(field)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <CustomFieldFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        field={editingField}
        onSuccess={refresh}
      />

      <AlertDialog open={!!deletingField} onOpenChange={() => setDeletingField(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Field</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingField?.label}"? Stored values for this field will also be removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default CustomFields
